import React from 'react';
import { Box } from '@mui/material';
import { Vehicle } from '../../types';
import { CONSIGNMENT_EXPOSURE_LABELS } from '../../types';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';

/** Nome e CNPJ da loja – revisar com o jurídico. Pode vir de configuração futura. */
const LOJA_NOME = 'NOME DA LOJA (CONSIGNATÁRIO)';
const LOJA_CNPJ = '00.000.000/0001-00';

interface ContratoConsignacaoDocumentProps {
  vehicle: Vehicle;
  /** Data de emissão do contrato (default: hoje) */
  dataEmissao?: string;
}

const formatCurrency = (value: number) =>
  value > 0 ? value.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' }) : 'Não aplicável (consignado)';

const formatDate = (dateString: string | undefined) => {
  if (!dateString) return '________________';
  try {
    const d = new Date(dateString);
    return isNaN(d.getTime()) ? '________________' : format(d, "dd 'de' MMMM 'de' yyyy", { locale: ptBR });
  } catch {
    return '________________';
  }
};

const ContratoConsignacaoDocument: React.FC<ContratoConsignacaoDocumentProps> = ({
  vehicle,
  dataEmissao = new Date().toISOString().split('T')[0],
}) => {
  const dataFormatada = formatDate(dataEmissao);
  const proprietario = vehicle.consignment_owner_name || '________________________________';
  const contato = vehicle.consignment_owner_contact || '________________________________';
  const comissao = vehicle.consignment_commission ?? 0;
  const comissaoTipo = vehicle.consignment_commission_type ?? 'percent';
  const exposicao = vehicle.consignment_exposure
    ? CONSIGNMENT_EXPOSURE_LABELS[vehicle.consignment_exposure]
    : '________________________________';
  const dataContrato = formatDate(vehicle.consignment_contract_date);

  return (
    <Box
      className="contrato-document"
      sx={{
        maxWidth: '210mm',
        width: '100%',
        margin: '0 auto',
        padding: { xs: 2, sm: '40px 50px' },
        fontFamily: '"Times New Roman", Georgia, serif',
        fontSize: 12,
        lineHeight: 1.6,
        color: '#000',
        '& .titulo': {
          textAlign: 'center',
          fontWeight: 'bold',
          fontSize: 16,
          marginBottom: 24,
          lineHeight: 1.3,
        },
        '& .partes': { marginBottom: 20 },
        '& .clausula': {
          textAlign: 'justify',
          marginBottom: 14,
          textIndent: 28,
        },
        '& .negrito': { fontWeight: 'bold' },
        '& .assinaturas': {
          marginTop: 48,
          display: 'flex',
          justifyContent: 'space-around',
          flexWrap: 'wrap',
          gap: 24,
        },
        '& .campo': { borderBottom: '1px solid #999', display: 'inline', px: 0.5 },
      }}
    >
      <p className="titulo">CONTRATO DE CONSIGNAÇÃO DE VEÍCULO</p>

      <p className="clausula">
        Pelo presente instrumento particular de <strong>Consignação de Veículo</strong>, as partes abaixo qualificadas
        celebram o seguinte contrato:
      </p>

      <div className="partes">
        <p className="clausula">
          <span className="negrito">CONSIGNANTE (PROPRIETÁRIO DO VEÍCULO):</span> {proprietario}, portador do CPF/CNPJ
          ________________, residente e domiciliado em ________________, telefone/contato: {contato}.
        </p>
        <p className="clausula">
          <span className="negrito">CONSIGNATÁRIO (LOJA):</span> {LOJA_NOME}, inscrita no CNPJ sob o nº {LOJA_CNPJ},
          com sede em ________________.
        </p>
      </div>

      <p className="clausula">
        <span className="negrito">DO OBJETO:</span> O CONSIGNANTE entrega em consignação ao CONSIGNATÁRIO o veículo
        abaixo descrito, para que este promova a venda a terceiros, sem que haja transferência de propriedade até a
        efetiva venda e pagamento.
      </p>

      <p className="clausula">
        <span className="negrito">DADOS DO VEÍCULO:</span> Marca: {vehicle.brand}; Modelo: {vehicle.model}; Ano:{' '}
        {vehicle.year}; Cor: {vehicle.color}; Placa: {vehicle.plate}; Chassi: {vehicle.chassis || '________________'};
        Quilometragem: {vehicle.km || '________________'}; Motor: {vehicle.motor || '________________'}; Câmbio:{' '}
        {vehicle.cambio || '________________'}.
      </p>

      <p className="clausula">
        <span className="negrito">FORMA DE EXPOSIÇÃO:</span> O veículo ficará {exposicao}, conforme combinado entre as
        partes.
      </p>

      <p className="clausula">
        <span className="negrito">VALOR MÍNIMO DE VENDA (PREÇO DE REFERÊNCIA):</span>{' '}
        {vehicle.purchase_price > 0 ? formatCurrency(vehicle.purchase_price) : 'A combinar entre as partes'}.
      </p>

      <p className="clausula">
        <span className="negrito">COMISSÃO:</span> O CONSIGNATÁRIO terá direito a comissão de{' '}
        <strong>
          {comissaoTipo === 'fixed'
            ? comissao.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })
            : `${comissao}%`}
        </strong>{' '}
        {comissaoTipo === 'fixed'
          ? 'a ser descontada no ato do repasse ao CONSIGNANTE.'
          : 'sobre o valor efetivo da venda, a ser descontada no ato do repasse ao CONSIGNANTE.'}
      </p>

      <p className="clausula">
        <span className="negrito">PRAZO:</span> O presente contrato tem vigência a partir de {dataContrato}, podendo ser
        prorrogado ou rescindido mediante acordo entre as partes, com comunicação por escrito.
      </p>

      <p className="clausula">
        <span className="negrito">RESPONSABILIDADES:</span> O CONSIGNATÁRIO compromete-se a zelar pelo veículo durante
        a permanência em suas dependências (quando aplicável), realizar divulgação e negociação com compradores. O
        CONSIGNANTE declara ser legítimo proprietário do veículo e que o mesmo está livre de ônus e impedimentos. As
        partes concordam que a venda somente se consumará com a assinatura de contrato de compra e venda e repasse do
        valor líquido ao CONSIGNANTE, após a dedução da comissão e eventuais despesas previamente acordadas.
      </p>

      <p className="clausula">
        <span className="negrito">LAUDO CAUTELAR / VISTORIA:</span> As partes atestam que o estado de conservação do
        veículo foi verificado e anotado em vistoria/laudo, quando aplicável, fazendo parte integrante do presente
        contrato.
      </p>

      {vehicle.consignment_notes && (
        <p className="clausula">
          <span className="negrito">OBSERVAÇÕES:</span> {vehicle.consignment_notes}
        </p>
      )}

      <p className="clausula">
        E, por estarem assim justos e contratados, firmam o presente em 2 (duas) vias de igual teor e forma.
      </p>

      <p className="clausula" style={{ marginTop: 28 }}>
        ________________________, {dataFormatada}.
      </p>

      <div className="assinaturas">
        <div style={{ textAlign: 'center' }}>
          <div style={{ borderTop: '1px solid #000', width: 220, marginTop: 48, paddingTop: 10, fontSize: 11 }}>
            CONSIGNANTE (Proprietário)
          </div>
        </div>
        <div style={{ textAlign: 'center' }}>
          <div style={{ borderTop: '1px solid #000', width: 220, marginTop: 48, paddingTop: 10, fontSize: 11 }}>
            CONSIGNATÁRIO (Loja)
          </div>
        </div>
      </div>

      <p style={{ marginTop: 16, fontSize: 10, color: '#666' }}>
        Documento gerado automaticamente. Recomenda-se revisão pelo setor jurídico antes do uso.
      </p>
    </Box>
  );
};

export default ContratoConsignacaoDocument;
